import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Fuel,
  Gauge,
  ParkingCircle,
  Plane,
  Route as RouteIcon,
  Ticket,
  TrafficCone,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/good-to-know";

const sections = [
  {
    icon: ParkingCircle,
    title: "Parking in Bucharest",
    points: [
      "Paid street parking in the centre is managed by the city app; zones 0–3 range roughly from 5 to 1.5 lei per hour.",
      "Underground garages (Piața Universității, Unirii, Victoriei) are the safest option overnight and usually 30–50 lei per day.",
      "Never park on tram tracks, in front of gates, or on marked green areas — towing is fast and expensive.",
      "Most hotels and malls offer free or discounted parking for guests — ask before you arrive.",
    ],
  },
  {
    icon: Gauge,
    title: "Driving rules & speed limits",
    points: [
      "Speed limits: 50 km/h in cities, 90 km/h on open roads, 100 km/h on expressways, 130 km/h on motorways.",
      "Blood alcohol limit is 0.00% — zero tolerance, always.",
      "Seat belts are mandatory for all passengers; children under 12 need an approved seat.",
      "Daytime running lights are required outside built-up areas all year round.",
      "Phone use without a hands-free kit is fined on the spot.",
    ],
  },
  {
    icon: Ticket,
    title: "Rovinieta (road vignette)",
    points: [
      "The rovinieta is required on all national roads and motorways outside cities.",
      "Every car in our fleet is delivered with a valid rovinieta included — you do not need to buy one.",
      "If you extend your rental, tell us and we renew it for you at no extra charge.",
      "Bridge toll at Fetești–Cernavodă (A2 towards the seaside) is paid separately.",
    ],
  },
  {
    icon: Plane,
    title: "Airport pickup (OTP)",
    points: [
      "We deliver to Henri Coandă (Otopeni) Arrivals — send us your flight number and we track delays.",
      "Meeting point is the short-term parking in front of Arrivals; handover takes about 10 minutes.",
      "Bucharest city centre is 17 km away, 25–45 minutes depending on traffic on DN1.",
      "Late-night and early-morning deliveries are available on request.",
    ],
  },
  {
    icon: TrafficCone,
    title: "Traffic & city tips",
    points: [
      "Rush hours are 08:00–10:00 and 17:00–19:30 — plan an extra 20 minutes.",
      "Piața Victoriei and Piața Unirii roundabouts are busy; stay in your lane and signal early.",
      "Trams always have priority; stop behind a tram when passengers board.",
      "Roads can be uneven outside the main boulevards — take speed bumps slowly.",
    ],
  },
  {
    icon: Fuel,
    title: "Fuel, tolls & payments",
    points: [
      "Petrol stations are plentiful (OMV, Petrom, Rompetrol, MOL) and almost all accept cards.",
      "Fuel is charged full-to-full: we hand the car over with a full tank and ask for the same on return.",
      "Contactless card payment works nearly everywhere; keep small cash for rural parking attendants.",
      "Emergency number is 112 — free from any phone.",
    ],
  },
];

export const Route = createFileRoute("/good-to-know")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title: "Good to Know: Driving in Bucharest | Car Rental Bucharest" },
      {
        name: "description",
        content:
          "Practical guide for driving in Bucharest: parking zones, speed limits, the rovinieta vignette, airport pickup tips, traffic and fuel advice.",
      },
      { property: "og:title", content: "Good to Know: Driving in Bucharest" },
      {
        property: "og:description",
        content:
          "Parking, driving rules, road vignette and airport tips for your car rental in Bucharest.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: GoodToKnowPage,
});

function GoodToKnowPage() {
  return (
    <main className="container-page py-14">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Good to Know: Driving in Bucharest",
            description:
              "Practical guide for driving in Bucharest: parking, speed limits, rovinieta, airport pickup and traffic tips.",
            mainEntityOfPage: CANONICAL,
            author: { "@type": "Organization", name: "Car Rental Bucharest" },
            publisher: { "@type": "Organization", name: "Car Rental Bucharest" },
          }),
        }}
      />

      <header className="max-w-2xl">
        <p className="eyebrow text-gold">Local guide</p>
        <h1 className="mt-3 font-display text-4xl font-bold leading-tight sm:text-5xl">
          Good to know in Bucharest
        </h1>
        <p className="mt-4 text-muted-foreground">
          Everything our guests ask before they pick up the keys — parking, road rules, the
          rovinieta, and how to get out of the airport smoothly.
        </p>
      </header>

      <div className="mt-12 grid gap-5 md:grid-cols-2">
        {sections.map((section) => (
          <section
            key={section.title}
            className="rounded-2xl border border-border bg-card p-6 shadow-card"
          >
            <div className="flex items-center gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-xl border border-gold/30 bg-gold/10">
                <section.icon className="size-5 text-gold" />
              </span>
              <h2 className="font-display text-lg font-bold">{section.title}</h2>
            </div>
            <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
              {section.points.map((point) => (
                <li key={point} className="flex gap-2.5">
                  <RouteIcon className="mt-0.5 size-3.5 shrink-0 text-gold" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <aside className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-gold/30 bg-gold/5 p-6">
        <AlertTriangle className="size-6 shrink-0 text-gold" />
        <p className="min-w-[16rem] flex-1 text-sm text-muted-foreground">
          Anything unclear? Our team is on the phone 7 days a week and can guide you through parking,
          routes, or an unexpected fine.
        </p>
        <Button variant="gold" asChild>
          <Link to="/rentals">Browse the fleet</Link>
        </Button>
      </aside>

      <section className="mt-10 rounded-2xl border border-border bg-card p-6 shadow-card">
        <h2 className="font-display text-xl font-bold">Related reading</h2>
        <ul className="mt-4 space-y-2.5 text-sm">
          <li>
            <Link
              to="/car-rental-no-deposit-bucharest"
              className="font-semibold text-gold hover:underline"
            >
              Car rental with no deposit in Bucharest
            </Link>
          </li>
          <li>
            <Link
              to="/rent-a-car-without-credit-card-bucharest"
              className="font-semibold text-gold hover:underline"
            >
              Rent a car without a credit card in Bucharest
            </Link>
          </li>
          <li>
            <Link
              to="/blog/$slug"
              params={{ slug: "cheap-car-rental-bucharest-otopeni-airport" }}
              className="font-semibold text-gold hover:underline"
            >
              Cheap car rental at Otopeni Airport
            </Link>
          </li>
          <li>
            <Link to="/blog" className="font-semibold text-gold hover:underline">
              All car rental guides
            </Link>
          </li>
        </ul>
      </section>

    </main>
  );
}
